import { useCallback } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CropMarks } from "@/components/common/CropMarks";
import { useBackground } from "@/contexts/BackgroundContext";
import { resetConsent } from "@/lib/analytics";
import type { LegalTabId } from "./LegalHero";
import { LEGAL_TABS } from "./LegalHero";

import privacyMd from "./content/privacy.md?raw";
import termsMd from "./content/terms.md?raw";
import disclosureMd from "./content/disclosure.md?raw";
import cookiesMd from "./content/cookies.md?raw";

const CONTENT: Record<LegalTabId, string> = {
  privacy: privacyMd,
  terms: termsMd,
  disclosure: disclosureMd,
  cookies: cookiesMd,
};

export function LegalContent({ activeTab }: { activeTab: LegalTabId }) {
  const activeLabel = LEGAL_TABS.find((t) => t.id === activeTab)?.label ?? "Legal";
  const md = CONTENT[activeTab];
  const { overlayOpacity } = useBackground();

  const handleResetPreferences = useCallback(() => {
    resetConsent();
    window.location.reload();
  }, []);
  const overlayColor = `rgba(var(--color-overlay-rgb),${overlayOpacity})`;

  return (
    <section
      id="legal-content"
      className="relative z-10 backdrop-blur-[2px] py-24 px-8"
      style={{
        background: overlayColor,
        transition: "background 1.5s ease-in-out",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-32 -translate-y-full pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, transparent, ${overlayColor})`,
          transition: "background 1.5s ease-in-out",
        }}
        aria-hidden="true"
      />
      <CropMarks />
      <div className="max-w-6xl mx-auto w-full">
        <div className="mb-12">
          <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            {activeLabel}
          </h2>
        </div>
        <div className="max-w-3xl legal-prose">
          <Markdown remarkPlugins={[remarkGfm]}>{md}</Markdown>
          {activeTab === "cookies" && (
            <div className="mt-12 pt-6 border-t border-[var(--color-border)]">
              <button
                onClick={handleResetPreferences}
                className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors cursor-pointer"
              >
                Reset cookie preferences →
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
